import type { LiveScanFrame } from "../stores/mappingScanStore";

/** Dedicated, lifecycle-scoped WS to device-control's /v1/maps-live/{buildId}.
 *  Bypasses the api-gateway (which doesn't proxy WS) — Vite dev-proxy / ingress
 *  routes it straight to device-control, mirroring /v1/realtime. */
export class MappingLiveClient {
  private ws?: WebSocket;
  private retryMs = 500;
  private timer?: number;
  private stopped = false;

  constructor(
    private buildId: string,
    private token: string | undefined,
    private onFrame: (f: LiveScanFrame) => void,
  ) {}

  connect(): void {
    this.stopped = false;
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${location.host}/v1/maps-live/${encodeURIComponent(this.buildId)}`;
    try {
      // Mirror the realtime client (ADR 91): the JWT rides the Sec-WebSocket-Protocol
      // header (["bearer", token]) — never the URL — and the server echoes "bearer" back
      // (SubProtocolCapable). Under security-on the old ?token= query form 401s the upgrade.
      this.ws = this.token
        ? new WebSocket(url, ["bearer", this.token])
        : new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws.onopen = () => { this.retryMs = 500; };
    this.ws.onmessage = (e) => {
      try { this.onFrame(JSON.parse(e.data as string)); } catch { /* ignore */ }
    };
    this.ws.onclose = () => { if (!this.stopped) this.scheduleReconnect(); };
    this.ws.onerror = () => this.ws?.close();
  }

  disconnect(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.ws?.close();
    this.ws = undefined;
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    const ms = Math.min(this.retryMs, 8000) + Math.random() * 200;
    this.retryMs = Math.min(this.retryMs * 2, 8000);
    this.timer = window.setTimeout(() => this.connect(), ms);
  }
}
